import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Crown, Trash2, AlertTriangle, History } from 'lucide-react';
import { signOut, deleteUser } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

function SubCard({ subInfo, tr, onUpgrade }) {
  if (!subInfo) return <div style={{ height: 56, background: 'rgba(255,255,255,0.04)', borderRadius: 12 }} className="skeleton" />;
  const { subscribed, inTrial, subscriptionPlan, subscriptionExpiresAt, subscriptionStartedAt, trialDaysLeft } = subInfo;
  if (subscribed) {
    const planName = subscriptionPlan === 'yearly' ? 'Yearly Plan' : 'Monthly Plan';
    return (
      <div style={{ padding: '14px 16px', borderRadius: 12, background: 'rgba(74,144,217,0.1)', border: '1px solid rgba(74,144,217,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Crown size={18} color="#4A90D9" />
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#F5F0E8' }}>{planName}</p>
              {subscriptionStartedAt && <p style={{ fontSize: 11, color: '#8A8580', marginTop: 1 }}>Started: {new Date(subscriptionStartedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
            </div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: 'rgba(74,144,217,0.2)', color: '#4A90D9', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Active</span>
        </div>
        {subscriptionExpiresAt && (
          <p style={{ fontSize: 11, color: '#8A8580', marginTop: 8 }}>
            Renews / expires: {new Date(subscriptionExpiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        )}
      </div>
    );
  }
  if (inTrial) {
    return (
      <div style={{ padding: '14px 16px', borderRadius: 12, background: 'rgba(45,106,79,0.1)', border: '1px solid rgba(45,106,79,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#F5F0E8' }}>Free Trial</p>
            <p style={{ fontSize: 11, color: '#8A8580', marginTop: 1 }}>{trialDaysLeft} days remaining</p>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: 'rgba(45,106,79,0.2)', color: '#2D6A4F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Trial</span>
        </div>
        <button onClick={onUpgrade} className="btn-primary" style={{ marginTop: 10, padding: '10px', fontSize: 13 }}>Upgrade Now</button>
      </div>
    );
  }
  return (
    <div style={{ padding: '14px 16px', borderRadius: 12, background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.2)' }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: '#C0392B' }}>No active subscription</p>
      <p style={{ fontSize: 11, color: '#8A8580', marginTop: 2 }}>Your trial has ended.</p>
      <button onClick={onUpgrade} className="btn-primary" style={{ marginTop: 10, padding: '10px', fontSize: 13 }}>Subscribe Now</button>
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();
  const { lang, setLang } = useLang();
  const tr = t[lang];

  const [birthTime,       setBirthTime]       = useState(profile?.birthTime || '');
  const [alarmTime,       setAlarmTime]        = useState(profile?.cosmicAlarmTime || '');
  const [saving,          setSaving]           = useState(false);
  const [saved,           setSaved]            = useState(false);
  const [error,           setError]            = useState('');
  const [subInfo,         setSubInfo]          = useState(null);
  const [transactions,    setTransactions]     = useState(null);
  const [showTxn,         setShowTxn]          = useState(false);
  const [showDeleteModal, setShowDeleteModal]  = useState(false);
  const [deleteConfirm,   setDeleteConfirm]    = useState('');
  const [deleting,        setDeleting]         = useState(false);

  useEffect(() => {
    api.get('/api/user/subscription').then(d => setSubInfo(d)).catch(() => {});
  }, []);

  const handleLangSwitch = async (newLang) => {
    setLang(newLang);
    try {
      await api.put('/api/user/settings', { language: newLang });
      await refreshProfile();
    } catch { /* silent */ }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.put('/api/user/settings', {
        birthTime:       birthTime || null,
        cosmicAlarmTime: alarmTime || null,
        language:        lang,
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { setError(err.message || 'Save failed.'); }
    setSaving(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const loadTransactions = async () => {
    if (transactions) { setShowTxn(v => !v); return; }
    try {
      const d = await api.get('/api/user/transactions');
      setTransactions(d.transactions || []);
      setShowTxn(true);
    } catch { setTransactions([]); setShowTxn(true); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm.toLowerCase() !== 'delete') return;
    setDeleting(true);
    try {
      await api.delete('/api/user/account');
      const fbUser = auth.currentUser;
      if (fbUser) await deleteUser(fbUser);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Account deletion failed. Please try again or contact support.');
      setShowDeleteModal(false);
    }
    setDeleting(false);
  };

  return (
    <div data-testid="settings-page" style={{ minHeight: '100vh', background: '#0A0F1E', padding: '64px 20px 80px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
        <button data-testid="settings-back-btn" onClick={() => navigate('/home')}
          style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, padding: 0 }}>
          <ChevronLeft size={18} />
        </button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#F5F0E8', marginLeft: 8 }}>
          {tr.settings || 'Settings'}
        </h1>
      </div>

      {/* Subscription */}
      <div className="card fade-in-up" style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
          Subscription
        </label>
        <SubCard subInfo={subInfo} tr={tr} onUpgrade={() => navigate('/paywall')} />

        {/* Transaction History */}
        <button onClick={loadTransactions} style={{
          marginTop: 12, background: 'none', border: 'none', color: '#C9A84C',
          cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, padding: 0,
        }}>
          <History size={13} /> {showTxn ? 'Hide' : 'View'} Transaction History
        </button>
        {showTxn && transactions !== null && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {transactions.length === 0 ? (
              <p style={{ fontSize: 12, color: '#8A8580' }}>No transactions yet.</p>
            ) : transactions.map((txn, i) => (
              <div key={i} style={{
                padding: '10px 12px', background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#F5F0E8', textTransform: 'capitalize' }}>{txn.plan} Plan</p>
                  <p style={{ fontSize: 10, color: '#8A8580' }}>{txn.createdAt ? new Date(txn.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#C9A84C' }}>₹{txn.amount}</p>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: 'rgba(45,106,79,0.2)', color: '#2D6A4F', textTransform: 'uppercase' }}>
                    {txn.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Language */}
        <div className="card fade-in-up-d1">
          <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>
            {tr.languagePref || 'Language'}
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            {['en','hi'].map(l => (
              <button key={l} type="button" data-testid={`lang-btn-${l}`} onClick={() => handleLangSwitch(l)}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  border: `1px solid ${lang === l ? '#C9A84C' : 'rgba(201,168,76,0.15)'}`,
                  background: lang === l ? 'rgba(201,168,76,0.1)' : 'transparent',
                  color: lang === l ? '#C9A84C' : '#8A8580',
                  cursor: 'pointer', fontSize: 14, fontWeight: 600, transition: 'all 0.2s',
                }}>
                {l === 'en' ? 'English' : 'हिंदी'}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 11, color: '#8A8580', marginTop: 8 }}>
            Changing language regenerates your daily reading in the selected language.
          </p>
        </div>

        {/* Birth time */}
        <div className="card fade-in-up-d2">
          <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
            {tr.birthTimeSetting || 'Birth Time'}
          </label>
          <input data-testid="settings-birth-time" type="time" className="input-field" value={birthTime} onChange={e => setBirthTime(e.target.value)} />
          <p style={{ fontSize: 11, color: '#8A8580', marginTop: 6 }}>Updates your birth chart with accurate Ascendant</p>
        </div>

        {/* Cosmic alarm */}
        <div className="card fade-in-up-d3">
          <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
            {tr.cosmicAlarmSetting || 'Cosmic Alarm'}
          </label>
          <input data-testid="settings-alarm-time" type="time" className="input-field" value={alarmTime} onChange={e => setAlarmTime(e.target.value)} />
          <p style={{ fontSize: 11, color: '#8A8580', marginTop: 6 }}>Get a daily notification to reveal your reading</p>
        </div>

        {error && <p style={{ color: '#C0392B', fontSize: 13, textAlign: 'center' }}>{error}</p>}
        {saved && <p data-testid="saved-notice" style={{ color: '#2D6A4F', fontSize: 13, textAlign: 'center' }}>✓ Saved</p>}

        <button data-testid="save-settings-btn" type="submit" className="btn-primary" disabled={saving}>
          {saving ? (tr.loading || 'Saving…') : (tr.saveChanges || 'Save Changes')}
        </button>

        <button data-testid="logout-btn" type="button" className="btn-secondary" onClick={handleLogout}
          style={{ color: '#C0392B', borderColor: 'rgba(192,57,43,0.4)' }}>
          {tr.logOut || 'Sign Out'}
        </button>
      </form>

      {/* Legal links */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12, marginTop: 24 }}>
        {[
          { label: 'Privacy Policy', path: '/privacy' },
          { label: 'Terms of Service', path: '/terms' },
          { label: 'About Jyoti', path: '/about-jyoti' },
          { label: 'Contact Support', path: 'mailto:support@dailyraashi.com' },
        ].map(link => (
          link.path.startsWith('mailto') ? (
            <a key={link.label} href={link.path} style={{ fontSize: 11, color: '#8A8580', textDecoration: 'none' }}>{link.label}</a>
          ) : (
            <button key={link.label} onClick={() => navigate(link.path)}
              style={{ background: 'none', border: 'none', color: '#8A8580', cursor: 'pointer', fontSize: 11 }}>
              {link.label}
            </button>
          )
        ))}
      </div>

      {/* Delete Account */}
      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <button
          data-testid="delete-account-btn"
          onClick={() => setShowDeleteModal(true)}
          style={{ background: 'none', border: 'none', color: 'rgba(192,57,43,0.6)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>
          Delete Account
        </button>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(8,12,26,0.92)',
          backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }} onClick={e => e.target === e.currentTarget && setShowDeleteModal(false)}>
          <div className="fade-in-up" style={{
            background: '#111827', border: '1px solid rgba(192,57,43,0.3)',
            borderRadius: '20px 20px 0 0', padding: '32px 24px 48px',
            width: '100%', maxWidth: 430,
          }}>
            <AlertTriangle size={28} color="#C0392B" style={{ display: 'block', margin: '0 auto 16px' }} />
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: '#F5F0E8', textAlign: 'center', marginBottom: 12 }}>
              Delete Account
            </h3>
            <p style={{ fontSize: 13, color: '#8A8580', textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
              This will permanently delete your account, birth chart, reading history and subscription. This action cannot be undone.
            </p>
            <p style={{ fontSize: 12, color: '#F5F0E8', marginBottom: 8 }}>
              Type <strong style={{ color: '#C0392B' }}>delete</strong> to confirm:
            </p>
            <input
              data-testid="delete-confirm-input"
              type="text"
              className="input-field"
              placeholder="delete"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            <button
              data-testid="confirm-delete-btn"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm.toLowerCase() !== 'delete'}
              style={{
                width: '100%', padding: '14px', borderRadius: 12,
                background: deleteConfirm.toLowerCase() === 'delete' ? 'rgba(192,57,43,0.8)' : 'rgba(192,57,43,0.2)',
                border: '1px solid rgba(192,57,43,0.4)', color: '#F5F0E8',
                cursor: deleteConfirm.toLowerCase() === 'delete' ? 'pointer' : 'not-allowed',
                fontSize: 15, fontWeight: 700, marginBottom: 10, transition: 'all 0.2s',
              }}>
              {deleting ? 'Deleting…' : 'Permanently Delete Account'}
            </button>
            <button className="btn-secondary" onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
